import { Inject, Injectable } from "@angular/core";
import { AbstractService } from "./abstract-service.service";
import { Address } from "../Models/address";
import { Client } from "../Models/client";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { APP_CONFIG, AppConfig } from "./config";
import { HttpClient, HttpParams } from "@angular/common/http";
import { AccountService } from "./account.service";
import { Response } from "../Models/response";
import { catchError, map, switchMap } from "rxjs/operators";
import { Town } from "../Models/town";
import { AddressType } from "../Models/address";

@Injectable({
  providedIn: "root",
})
export class AddressService extends AbstractService<Address, number> {
  url: string;
  townUrl: string;

  private client?: Client;
  public addressSubject: BehaviorSubject<Address[]>;

  constructor(
    http: HttpClient,
    @Inject(APP_CONFIG) config: AppConfig,
    private accountService: AccountService,
  ) {
    super(http);
    this.url = config.apiEndpoint.concat("/new/addresses");
    this.townUrl = config.apiEndpoint.concat("/new/town");

    this.addressSubject = new BehaviorSubject<Address[]>([]);

    this.accountService.accountSubject.subscribe((client) => {
      if (client) {
        this.client = client;
        // get addresses
        this.getByClientId(this.client.id!).subscribe((response) => {
          this.addressSubject.next(response!);
        });
      } else {
        this.addressSubject.next([]);
      }
    });
  }

  public getByClientId(clientId: number): Observable<Address[]> {
    const url = `${this.url}/client/${clientId}`;
    return this.http.get<Address[]>(url, { withCredentials: true }).pipe(
      map((response: Address[]) => {
        if (response) {
          return response;
        } else {
          throw new Error();
        }
      }),
    );
  }

  public getTownsByWilayaId(wilayaId: number): Observable<Town[]> {
    const options = {
      params: new HttpParams().append("wilayaId", wilayaId.toString()),

      withCredentials: true,
    };
    const url = `${this.townUrl}/wilaya`;
    return this.http.get<Town[]>(url, options).pipe(
      map((response: Town[]) => {
        if (response) {
          return response;
        } else {
          throw new Error();
        }
      }),
    );
  }

  public getAddressById(addressId: number): Address {
    return this.addressSubject.value.filter(
      (address) => address.id === addressId,
    )[0];
  }

  public addAddress(address: any): Observable<Address> {
    return this.http
      .post<number>(this.url, address, { withCredentials: true })
      .pipe(
        switchMap((id) =>
          this.getAdrById(id).pipe(
            map((newAdr: Address) => {
              const current = this.addressSubject.value;
              this.addressSubject.next([...current, newAdr]);
              return newAdr;
            }),
          ),
        ),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  public remove(address: Address): Observable<Address[]> {
    const url = this.url.concat("/").concat(String(address.id));
    return this.http.delete(url, { withCredentials: true }).pipe(
      map(() => {
        const adr = this.addressSubject.value.filter(
          (item) => item.id !== address.id,
        );
        this.addressSubject.next(adr);
        return adr;
      }),
    );
  }

  public updateAdr(address: Address): Observable<Address[]> {
    return this.http.put(this.url, address, { withCredentials: true }).pipe(
      map(() => {
        const adr = this.addressSubject.value.map((item) =>
          item.id === address.id ? address : item,
        );
        this.addressSubject.next(adr);
        return adr;
      }),
    );
  }

  public getAdrById(id: number): Observable<Address> {
    const url = this.url.concat("/").concat(String(id));
    return this.http.get<Address>(url, { withCredentials: true });
  }
}
